import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface HubSpotFormConfig {
  portalId: string;
  formId: string;
  region?: string;
  target?: string;
  defaultValues?: Record<string, string>;
}

interface HubSpotContextType {
  showForm: (config: HubSpotFormConfig) => void;
  hideForm: () => void;
  isFormVisible: boolean;
}

const HubSpotContext = createContext<HubSpotContextType | undefined>(undefined);

interface HubSpotProviderProps {
  children: ReactNode;
}

export const HubSpotProvider: React.FC<HubSpotProviderProps> = ({ children }) => {
  const [isFormVisible, setIsFormVisible] = useState(false);
  const [formConfig, setFormConfig] = useState<HubSpotFormConfig | null>(null);
  const [scriptLoaded, setScriptLoaded] = useState(false);

  // Load HubSpot script once when provider mounts
  useEffect(() => {
    // Check if HubSpot is already loaded
    if ((window as any).hbspt) {
      setScriptLoaded(true);
      return;
    }

    const script = document.createElement('script');
    script.src = '//js.hsforms.net/forms/embed/v2.js';
    script.charset = 'utf-8';
    script.type = 'text/javascript';
    script.async = true;
    script.onload = () => {
      setScriptLoaded(true);
    };
    document.body.appendChild(script);

    return () => {
      // Only remove if we added it and it's still in the DOM
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, []);

  // Initialize form when config changes and script is loaded
  useEffect(() => {
    if (isFormVisible && formConfig && scriptLoaded && (window as any).hbspt) {
      // Clear previous form
      const container = document.getElementById('hubspot-form-container');
      if (container) {
        container.innerHTML = '';
      }

      // Small delay to ensure DOM is ready
      setTimeout(() => {
        (window as any).hbspt.forms.create({
          portalId: formConfig.portalId,
          formId: formConfig.formId,
          region: formConfig.region || 'na1',
          target: formConfig.target || '#hubspot-form-container',
          onFormReady: function($form) {
            // Set default values if provided
            if (formConfig.defaultValues) {
              if ($form && $form.length !== undefined) {
                for (let i = 0; i < $form.length; i++) {
                  const element = $form[i];
                  // Check if this element contains the email input (fieldset case)
                  if (element && element.querySelector) {
                    Object.entries(formConfig.defaultValues).forEach(([fieldName, defaultValue]) => {
                      const fieldInput = element.querySelector(`input[name="${fieldName}"]`);
                      if (fieldInput) {
                        (fieldInput as HTMLInputElement).value = String(defaultValue);
                        // Trigger change event to ensure HubSpot recognizes the value
                        fieldInput.dispatchEvent(new Event('change', { bubbles: true }));
                        fieldInput.dispatchEvent(new Event('input', { bubbles: true }));
                      }
                    });
                  }
                }
              }
            }
          }
        });
      }, 100);
    }
  }, [isFormVisible, formConfig, scriptLoaded]);

  const showForm = (config: HubSpotFormConfig) => {
    setFormConfig(config);
    setIsFormVisible(true);
    // Prevent body scroll when modal is open
    document.body.classList.add('no-scroll');
  };

  const hideForm = () => {
    setIsFormVisible(false);
    setFormConfig(null);
    // Re-enable body scroll
    document.body.classList.remove('no-scroll');
  };

  // Handle ESC key to close modal
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isFormVisible) {
        hideForm();
      }
    };

    if (isFormVisible) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isFormVisible]);

  return (
    <HubSpotContext.Provider value={{ showForm, hideForm, isFormVisible }}>
      {children}
      
      {/* Global Modal */}
      {isFormVisible && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
          onClick={(e) => {
            // Close modal if clicking on backdrop
            if (e.target === e.currentTarget) {
              hideForm();
            }
          }}
        >
          <div className="bg-white rounded-lg p-6 w-full max-w-xl max-h-[90vh] overflow-y-auto relative mx-4">
            <button 
              onClick={hideForm}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 transition-colors z-10"
              aria-label="Close form"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
            <div id="hubspot-form-container"></div>
          </div>
        </div>
      )}
    </HubSpotContext.Provider>
  );
};

export const useHubSpot = () => {
  const context = useContext(HubSpotContext);
  if (context === undefined) {
    throw new Error('useHubSpot must be used within a HubSpotProvider');
  }
  return context;
};